import type { Resource } from '@/types/resource';

const UPLOAD_NAME_EXTENSION_BY_MEDIA_KIND: Readonly<Record<string, RegExp>> = {
  image: /\.(?:png|jpe?g|gif|webp|bmp|svg)$/i,
  video: /\.(?:mp4|mov|avi|webm)$/i,
  audio: /\.(?:mp3|wav|ogg|aac|m4a|webm)$/i,
};

/** 媒体的默认业务名称不含格式后缀；原始 File 和手动名称由调用方保留。 */
export function getDefaultMediaUploadName(file: Pick<File, 'name' | 'type'>): string {
  const mediaKind = file.type.toLowerCase().split('/', 1)[0];
  const extensionPattern = UPLOAD_NAME_EXTENSION_BY_MEDIA_KIND[mediaKind];
  if (!extensionPattern) return file.name;
  return file.name.replace(extensionPattern, '') || file.name;
}

export type ProjectResourceMediaInput = {
  mediaKind?: 'image' | 'video' | 'audio' | null;
  originalFileName?: string | null;
  duration?: number | null;
  frameRate?: number | null;
  format?: string | null;
  metadata?: Record<string, unknown>;
};

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function readMediaKind(value: unknown): Resource['mediaKind'] {
  return value === 'image' || value === 'video' || value === 'audio' ? value : undefined;
}

export function mergeProjectResourceMediaMetadata(
  item: ProjectResourceMediaInput
): Pick<
  Resource,
  'mediaKind' | 'originalFileName' | 'duration' | 'frameRate' | 'format' | 'metadata'
> {
  const metadata = item.metadata ?? {};
  const mediaKind = readMediaKind(item.mediaKind ?? metadata.mediaKind ?? metadata.media_kind);
  const originalFileName = readString(
    item.originalFileName ?? metadata.originalFileName ?? metadata.original_file_name
  );
  const duration = readNumber(item.duration ?? metadata.duration);
  const frameRate = readNumber(item.frameRate ?? metadata.frameRate ?? metadata.frame_rate);
  const format = readString(item.format ?? metadata.format);

  return {
    mediaKind,
    originalFileName,
    duration,
    frameRate,
    format,
    metadata: {
      ...metadata,
      ...(mediaKind ? { mediaKind } : {}),
      ...(originalFileName ? { originalFileName } : {}),
      ...(duration != null ? { duration } : {}),
      ...(frameRate != null ? { frameRate } : {}),
      ...(format ? { format } : {}),
    },
  };
}
